import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import toast from 'react-hot-toast';

const { FiFilter, FiSearch, FiCheck, FiMail, FiPhone } = FiIcons;

const InquiryManagement = () => {
  const { inquiries, updateInquiryStatus } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const filteredInquiries = inquiries
    .filter(inquiry => {
      const matchesSearch = searchTerm === '' || 
        inquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inquiry.vehicleName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'oldest') {
        return new Date(a.created_at) - new Date(b.created_at);
      }
      return new Date(b.created_at) - new Date(a.created_at); // newest
    });

  const handleUpdateStatus = (id, status) => {
    updateInquiryStatus(id, status);
    toast.success('Estado de la consulta actualizado');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2c3e50]">Gestión de Consultas</h1>
          <p className="text-gray-600 mt-2">Total: {filteredInquiries.length} consultas</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre, email, vehículo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="responded">Respondidas</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
          >
            <option value="newest">Más Recientes</option>
            <option value="oldest">Más Antiguas</option>
          </select>
          
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setSortBy('newest');
            }}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      {filteredInquiries.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No se encontraron consultas</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'Intenta ajustar tus filtros de búsqueda' 
              : 'Aún no has recibido ninguna consulta'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredInquiries.map((inquiry, index) => (
            <motion.div
              key={inquiry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="flex flex-col md:flex-row justify-between md:items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#2c3e50]">{inquiry.name}</h3>
                  <p className="text-gray-600">
                    Interesado en: <span className="font-medium text-[#005c91]">{inquiry.vehicleName}</span>
                  </p>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <a href={`mailto:${inquiry.email}`} className="flex items-center space-x-1 hover:text-[#005c91]">
                      <SafeIcon icon={FiMail} />
                      <span>{inquiry.email}</span>
                    </a>
                    <a href={`tel:${inquiry.phone}`} className="flex items-center space-x-1 hover:text-[#005c91]">
                      <SafeIcon icon={FiPhone} />
                      <span>{inquiry.phone}</span>
                    </a>
                  </div>
                </div>
                
                <div className="mt-4 md:mt-0 text-right">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    inquiry.status === 'pending' 
                      ? 'bg-orange-100 text-orange-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {inquiry.status === 'pending' ? 'Pendiente' : 'Respondido'}
                  </span>
                  <p className="text-sm text-gray-500 mt-2">
                    {new Date(inquiry.created_at).toLocaleString('es-ES')}
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700">{inquiry.message}</p>
              </div>
              
              {inquiry.status === 'pending' && (
                <div className="flex justify-end">
                  <button
                    onClick={() => handleUpdateStatus(inquiry.id, 'responded')}
                    className="bg-[#27ae60] hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    <span>Marcar como respondido</span>
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InquiryManagement;